import React, { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import {
  FaBan,
  FaCheck,
  FaEdit,
  FaEye,
  FaPaperPlane,
  FaPlay,
  FaRedo,
  FaTrash,
  FaUserPlus,
} from "react-icons/fa";
import { useConfirm, useNotify } from "../../components/ConfirmModal";
import ReadyPack from "./ReadyPack";
import TrackingBox from "./TrackingBox";
import { AreaChip, Avatar, Drawer, PersonSelect, StatusPill } from "./WorkUi";
import { errorMessage, fmtShortDate, fmtTime, nullIfBlank, relativeDay, statusLabel } from "./workApi";

const KIND_TEXT = {
  created: "created this",
  comment: "commented",
  status: "changed status",
  review: "reviewed",
  assign: "changed people",
  dates: "changed dates",
  posted: "marked posted",
  daily: "posted a daily update",
  publish: "publishing",
};

function activityText(a) {
  if (a.Kind === "status" && a.Meta) return `${statusLabel(a.Meta.from)} → ${statusLabel(a.Meta.to)}`;
  if (a.Kind === "review" && a.Meta) return a.Meta.decision === "approve" ? "Approved" : "Asked for changes";
  if (a.Kind === "dates" && a.Meta) {
    return Object.entries(a.Meta)
      .map(([k, [from, to]]) => `${k.replace(/_/g, " ")}: ${from ? fmtShortDate(from) : "—"} → ${to ? fmtShortDate(to) : "—"}`)
      .join("; ");
  }
  if (a.Kind === "publish" && a.Meta?.failed) return `Auto-publish to ${a.Meta.platform} failed`;
  if (a.Kind === "publish" && a.Meta?.accounts) return `Publishing to ${a.Meta.accounts.join(", ")} (${a.Meta.when})`;
  return null;
}

function ItemDrawer({ api, itemId, meta, onClose, onChanged, openPack = false }) {
  const notify = useNotify();
  const confirm = useConfirm();
  const [item, setItem] = useState(null);
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const [editing, setEditing] = useState(false);
  const [form, setForm] = useState({});
  const [note, setNote] = useState("");
  const [comment, setComment] = useState("");
  const [packOpen, setPackOpen] = useState(openPack);
  const [person, setPerson] = useState({ User_Id: null, Role: "Watcher" });
  const [people, setPeople] = useState(meta?.people || []);
  const [engagement, setEngagement] = useState(null);
  const today = meta?.today;

  useEffect(() => {
    if (item?.Status !== "Posted") return;
    api.itemEngagement(itemId).then(setEngagement).catch(() => setEngagement(null));
  }, [api, itemId, item?.Status]);

  const load = useCallback(async () => {
    try {
      const data = await api.item(itemId);
      setItem(data);
      setError("");
      if (data?.can_manage && data.Project_Type) {
        const m = await api.meta({ project_type: data.Project_Type, project_id: data.Project_Id }).catch(() => null);
        if (m?.people) setPeople(m.people);
      }
    } catch (err) {
      setError(errorMessage(err, "Could not load this item"));
    }
  }, [api, itemId]);

  useEffect(() => {
    load();
  }, [load]);

  const changed = (updated) => {
    onChanged?.(updated);
    load();
  };

  const run = async (fn, success) => {
    setBusy(true);
    setError("");
    try {
      const res = await fn();
      if (success) notify.success(success);
      changed(res);
      return true;
    } catch (err) {
      setError(errorMessage(err));
      return false;
    } finally {
      setBusy(false);
    }
  };

  const setStatus = (status, extra = {}) => run(() => api.updateItem(itemId, { Status: status, ...extra }), statusLabel(status));

  const stuck = async () => {
    if (!note.trim()) {
      setError("Tell your team what is blocking you (type it in the note box), then tap I'm stuck.");
      return;
    }
    if (await setStatus("Blocked", { Note: note.trim() })) setNote("");
  };

  const review = async (decision) => {
    if (decision === "changes" && !note.trim()) {
      setError("Write what needs to change in the note box.");
      return;
    }
    if (await run(() => api.review(itemId, decision, note.trim() || null), decision === "approve" ? "Approved" : "Sent back for changes")) {
      setNote("");
    }
  };

  const startEdit = () => {
    setForm({
      Title: item.Title,
      Item_Type: item.Item_Type,
      Description: item.Description || "",
      Executor_Id: item.Executor_Id,
      Reviewer_Id: item.Reviewer_Id,
      Priority: item.Priority,
      Due_Date: item.Due_Date || "",
      Est_Done_Date: item.Est_Done_Date || "",
      Planned_Post_Date: item.Planned_Post_Date || "",
      Planned_Post_Time: item.Planned_Post_Time || "",
      Platform: item.Platform || "",
      Caption: item.Caption || "",
      Media_URL: item.Media_URL || "",
      Progress_Pct: item.Progress_Pct || 0,
    });
    setEditing(true);
  };

  const saveEdit = async (e) => {
    e.preventDefault();
    const managerFields = ["Title", "Item_Type", "Executor_Id", "Reviewer_Id", "Priority", "Due_Date"];
    const body = {};
    Object.entries(form).forEach(([k, v]) => {
      if (!item.can_manage && managerFields.includes(k)) return;
      body[k] = typeof v === "string" ? nullIfBlank(v.trim()) : v;
    });
    if (await run(() => api.updateItem(itemId, body), "Saved")) setEditing(false);
  };

  const remove = async () => {
    if (!(await confirm({ title: "Delete item", message: `Delete "${item.Title}"? Its history goes too.` }))) return;
    try {
      await api.deleteItem(itemId);
      notify.success("Deleted");
      onChanged?.(null);
      onClose();
    } catch (err) {
      setError(errorMessage(err));
    }
  };

  const sendComment = async (e) => {
    e.preventDefault();
    if (!comment.trim()) return;
    if (await run(() => api.comment(itemId, comment.trim()))) setComment("");
  };

  if (!item) {
    return (
      <Drawer title="Work item" onClose={onClose}>
        {error ? <div className="dmp-error">{error}</div> : <div className="wk-muted">Loading…</div>}
      </Drawer>
    );
  }

  const canAct = item.is_executor || item.can_manage;
  const closed = item.Is_Closed;
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  return (
    <Drawer title={item.Title} onClose={onClose}>
      <div className="wk-detail-head">
        <StatusPill status={item.Status} />
        <AreaChip name={item.Area_Name} color={item.Area_Color} icon={item.Area_Icon} />
        <span className="wk-muted">{item.Item_Type}</span>
        {item.Is_Overdue ? <span className="wk-count wk-count--red">Late</span> : null}
      </div>
      {item.Project_Name ? (
        <p className="wk-muted" style={{ marginTop: -4 }}>
          <Link to={api.link(`work/plan/${item.Project_Type}/${item.Project_Id}`)} onClick={onClose}>
            {item.Project_Name}
          </Link>
          {item.Tenant_Name ? ` · ${item.Tenant_Name}` : ""}
        </p>
      ) : null}
      {error ? <div className="dmp-error">{error}</div> : null}

      {canAct && !closed ? (
        <>
          <label className="wk-field">
            Note (optional; required for "I'm stuck")
            <textarea value={note} onChange={(e) => setNote(e.target.value)} placeholder="What moved, what is pending…" />
          </label>
          <div className="wk-actions-bar">
            {item.Status !== "In Progress" ? (
              <button type="button" className="dmp-btn dmp-btn--ghost wk-btn-lg" disabled={busy} onClick={() => setStatus("In Progress", { Note: note || undefined })}>
                <FaPlay aria-hidden="true" /> Start working
              </button>
            ) : null}
            {item.Status !== "In Review" && !["Approved", "Scheduled"].includes(item.Status) ? (
              <button type="button" className="dmp-btn wk-btn-lg" disabled={busy} onClick={() => setStatus("In Review", { Note: note || undefined })}>
                <FaEye aria-hidden="true" /> Send for review
              </button>
            ) : null}
            {["Approved", "Scheduled"].includes(item.Status) || item.Planned_Post_Date ? (
              <button type="button" className="dmp-btn wk-btn-lg" onClick={() => setPackOpen(true)}>
                <FaPaperPlane aria-hidden="true" /> Ready to post
              </button>
            ) : null}
            {item.Status !== "Blocked" ? (
              <button type="button" className="dmp-btn dmp-btn--danger wk-btn-lg" disabled={busy} onClick={stuck}>
                <FaBan aria-hidden="true" /> I'm stuck
              </button>
            ) : null}
            {!item.Planned_Post_Date ? (
              <button type="button" className="dmp-btn dmp-btn--ghost wk-btn-lg" disabled={busy} onClick={() => setStatus("Done", { Note: note || undefined })}>
                <FaCheck aria-hidden="true" /> Mark done
              </button>
            ) : null}
          </div>
        </>
      ) : null}

      {item.can_review && item.Status === "In Review" ? (
        <div className="wk-banner" style={{ display: "block" }}>
          <strong>Waiting for your review.</strong>
          <label className="wk-field" style={{ marginTop: 8 }}>
            Feedback (required when asking for changes)
            <textarea value={note} onChange={(e) => setNote(e.target.value)} />
          </label>
          <div className="wk-actions-bar" style={{ marginBottom: 0 }}>
            <button type="button" className="dmp-btn wk-btn-lg" disabled={busy} onClick={() => review("approve")}>
              <FaCheck aria-hidden="true" /> Approve
            </button>
            <button type="button" className="dmp-btn dmp-btn--danger wk-btn-lg" disabled={busy} onClick={() => review("changes")}>
              <FaRedo aria-hidden="true" /> Ask for changes
            </button>
          </div>
        </div>
      ) : null}

      {!editing ? (
        <>
          <dl className="wk-kv">
            <dt>Doing it</dt>
            <dd>
              <Avatar name={item.Executor_Name} size={20} /> {item.Executor_Name || "Unassigned"}
            </dd>
            <dt>Reviewer</dt>
            <dd>
              {item.Effective_Reviewer_Name || "—"}
              {!item.Reviewer_Id && item.Effective_Reviewer_Name ? <span className="wk-muted"> (from lane / owner)</span> : null}
            </dd>
            <dt>Due</dt>
            <dd>{item.Due_Date ? `${fmtShortDate(item.Due_Date)} · ${relativeDay(item.Due_Date, today)}` : "—"}</dd>
            <dt>Estimated finish</dt>
            <dd>{item.Est_Done_Date ? fmtShortDate(item.Est_Done_Date) : "—"}</dd>
            <dt>Post on</dt>
            <dd>
              {item.Planned_Post_Date ? fmtShortDate(item.Planned_Post_Date) : "—"}
              {item.Planned_Post_Time ? ` at ${fmtTime(item.Planned_Post_Time)}` : ""}
              {item.Platform ? ` · ${item.Platform}` : ""}
            </dd>
            <dt>Priority</dt>
            <dd>{item.Priority}</dd>
            <dt>Progress</dt>
            <dd>{item.Progress_Pct || 0}%</dd>
            {item.Approved_By_Name ? (
              <>
                <dt>Approved by</dt>
                <dd>
                  {item.Approved_By_Name} · {fmtShortDate(item.Approved_On)}
                </dd>
              </>
            ) : null}
            {item.Posted_URL ? (
              <>
                <dt>Live link</dt>
                <dd>
                  <a href={item.Posted_URL} target="_blank" rel="noreferrer">
                    Open post
                  </a>
                </dd>
              </>
            ) : null}
            {item.Description ? (
              <>
                <dt>Brief</dt>
                <dd style={{ whiteSpace: "pre-wrap" }}>{item.Description}</dd>
              </>
            ) : null}
          </dl>
          {canAct ? (
            <div className="wk-actions-bar">
              <button type="button" className="dmp-btn dmp-btn--ghost" onClick={startEdit}>
                <FaEdit aria-hidden="true" /> Edit details
              </button>
              {item.can_manage ? (
                <button type="button" className="dmp-btn dmp-btn--danger" onClick={remove}>
                  <FaTrash aria-hidden="true" /> Delete
                </button>
              ) : null}
            </div>
          ) : null}
        </>
      ) : (
        <form onSubmit={saveEdit}>
          {item.can_manage ? (
            <>
              <label className="wk-field">
                Title
                <input value={form.Title} onChange={set("Title")} required maxLength={200} />
              </label>
              <div className="wk-row">
                <label className="wk-field">
                  Type
                  <select value={form.Item_Type} onChange={set("Item_Type")}>
                    {(meta?.item_types || [form.Item_Type]).map((t) => (
                      <option key={t}>{t}</option>
                    ))}
                  </select>
                </label>
                <label className="wk-field">
                  Priority
                  <select value={form.Priority} onChange={set("Priority")}>
                    {["P1", "P2", "P3", "P4"].map((p) => (
                      <option key={p}>{p}</option>
                    ))}
                  </select>
                </label>
              </div>
              <div className="wk-row">
                <label className="wk-field">
                  Doing it
                  <PersonSelect people={people} value={form.Executor_Id} onChange={(v) => setForm((f) => ({ ...f, Executor_Id: v }))} blank="Unassigned" />
                </label>
                <label className="wk-field">
                  Reviewer
                  <PersonSelect people={people} value={form.Reviewer_Id} onChange={(v) => setForm((f) => ({ ...f, Reviewer_Id: v }))} blank="Use lane reviewer" />
                </label>
              </div>
              <label className="wk-field">
                Due date
                <input type="date" value={form.Due_Date} onChange={set("Due_Date")} />
              </label>
            </>
          ) : null}
          <div className="wk-row">
            <label className="wk-field">
              Estimated finish
              <input type="date" value={form.Est_Done_Date} onChange={set("Est_Done_Date")} />
            </label>
            <label className="wk-field">
              Progress %
              <input type="number" min="0" max="100" value={form.Progress_Pct} onChange={set("Progress_Pct")} />
            </label>
          </div>
          <div className="wk-row">
            <label className="wk-field">
              Post date
              <input type="date" value={form.Planned_Post_Date} onChange={set("Planned_Post_Date")} />
            </label>
            <label className="wk-field">
              Post time
              <input type="time" value={form.Planned_Post_Time} onChange={set("Planned_Post_Time")} />
            </label>
            <label className="wk-field">
              Platform
              <select value={form.Platform} onChange={set("Platform")}>
                <option value="">—</option>
                {(meta?.platforms || []).map((p) => (
                  <option key={p}>{p}</option>
                ))}
              </select>
            </label>
          </div>
          <label className="wk-field">
            Caption
            <textarea value={form.Caption} onChange={set("Caption")} />
          </label>
          <label className="wk-field">
            Media link
            <input type="url" value={form.Media_URL} onChange={set("Media_URL")} placeholder="https://…" />
          </label>
          <label className="wk-field">
            Brief / notes
            <textarea value={form.Description} onChange={set("Description")} />
          </label>
          <div className="wk-actions-bar">
            <button type="submit" className="dmp-btn wk-btn-lg" disabled={busy}>
              Save
            </button>
            <button type="button" className="dmp-btn dmp-btn--ghost wk-btn-lg" onClick={() => setEditing(false)}>
              Cancel
            </button>
          </div>
        </form>
      )}

      {item.Status === "Posted" && item.tracking && item.tracking.status !== "none" ? (
        <TrackingBox
          key={`${item.tracking.status}-${item.Posted_URL || ""}`}
          tracking={item.tracking}
          postedUrl={item.Posted_URL}
          onTrack={(url) => api.trackItem(itemId, url)}
          onChanged={load}
        />
      ) : item.Status === "Posted" && item.tracking?.status === "none" && (item.Platform || item.Posted_URL) ? (
        <TrackingBox
          tracking={{ status: "none", message: "Paste the live post link to sync its numbers and comments." }}
          postedUrl={item.Posted_URL}
          onTrack={(url) => api.trackItem(itemId, url)}
          onChanged={load}
        />
      ) : null}

      {engagement ? (
        <>
          <div className="wk-h3">Results</div>
          <div className="wk-person__counts" style={{ marginBottom: 8 }}>
            {[
              ["Views", engagement.totals.Views],
              ["Reach", engagement.totals.Reach],
              ["Likes", engagement.totals.Likes],
              ["Comments", engagement.totals.Comments],
              ["Shares", engagement.totals.Shares],
              ["Clicks", engagement.totals.Clicks],
            ]
              .filter(([, v]) => v)
              .map(([label, v]) => (
                <span key={label} className="wk-count">
                  {Number(v).toLocaleString("en-IN")} {label.toLowerCase()}
                </span>
              ))}
            {!Object.values(engagement.totals).some(Boolean) ? (
              <span className="wk-muted">No numbers yet. They sync every hour after posting through a connected account.</span>
            ) : null}
          </div>
          {engagement.comments ? (
            <Link to={api.link(`work/comments?item=${itemId}`)} onClick={onClose}>
              {engagement.new_comments ? `${engagement.new_comments} comment(s) waiting for a reply` : `${engagement.comments} comment(s)`}
            </Link>
          ) : null}
        </>
      ) : null}

      <div className="wk-h3">People</div>
      <ul className="wk-timeline">
        {item.people.map((p) => (
          <li key={p.Assignment_Id}>
            <Avatar name={p.Name} size={22} /> <span style={{ flex: 1 }}>{p.Name}</span>
            <span className="wk-muted">{p.Role}</span>
            {item.can_manage ? (
              <button type="button" className="dmp-link" onClick={() => run(() => api.removePerson(itemId, p.Assignment_Id))}>
                Remove
              </button>
            ) : null}
          </li>
        ))}
      </ul>
      {item.can_manage ? (
        <div className="wk-row" style={{ alignItems: "end" }}>
          <label className="wk-field">
            Add person
            <PersonSelect people={people} value={person.User_Id} onChange={(v) => setPerson((p) => ({ ...p, User_Id: v }))} />
          </label>
          <label className="wk-field">
            As
            <select value={person.Role} onChange={(e) => setPerson((p) => ({ ...p, Role: e.target.value }))}>
              {["Watcher", "Approver", "Reviewer", "Executor"].map((r) => (
                <option key={r}>{r}</option>
              ))}
            </select>
          </label>
          <div className="wk-field">
            <span className="wk-sr-only">Add</span>
            <button
              type="button"
              className="dmp-btn"
              disabled={!person.User_Id || busy}
              onClick={async () => {
                if (await run(() => api.addPerson(itemId, person.User_Id, person.Role), "Added")) setPerson({ User_Id: null, Role: "Watcher" });
              }}
            >
              <FaUserPlus aria-hidden="true" /> Add
            </button>
          </div>
        </div>
      ) : null}

      {item.daily_history?.length ? (
        <>
          <div className="wk-h3">Daily updates</div>
          <ul className="wk-timeline">
            {item.daily_history.map((d) => (
              <li key={d.Daily_Status_Id}>
                <span className="wk-timeline__when">{fmtShortDate(d.Status_Date)}</span>
                <span style={{ flex: 1 }}>
                  <strong>{d.User_Name}</strong> · {d.Status_Label || "—"}
                  {d.Progress_Pct != null ? ` · ${d.Progress_Pct}%` : ""}
                  {d.Note ? <div>{d.Note}</div> : null}
                  {d.Blocker ? <div style={{ color: "#b91c1c" }}>Stuck: {d.Blocker}</div> : null}
                  {d.Slipped ? (
                    <div className="wk-muted">
                      Post date moved {fmtShortDate(d.Prev_Est_Post_Date)} → {fmtShortDate(d.Est_Post_Date)}
                    </div>
                  ) : null}
                </span>
              </li>
            ))}
          </ul>
        </>
      ) : null}

      <div className="wk-h3">Comments and history</div>
      <form onSubmit={sendComment} className="wk-quick-add" style={{ marginBottom: 10 }}>
        <input value={comment} onChange={(e) => setComment(e.target.value)} placeholder="Write a comment…" aria-label="Comment" />
        <button type="submit" className="dmp-btn" disabled={!comment.trim() || busy}>
          Send
        </button>
      </form>
      <ul className="wk-timeline">
        {item.activity.map((a) => (
          <li key={a.Activity_Id}>
            <Avatar name={a.User_Name || "System"} size={22} />
            <span style={{ flex: 1 }}>
              <strong>{a.User_Name || "System"}</strong> {KIND_TEXT[a.Kind] || a.Kind}
              {activityText(a) ? <div className="wk-muted">{activityText(a)}</div> : null}
              {a.Body ? <div style={{ whiteSpace: "pre-wrap" }}>{a.Body}</div> : null}
            </span>
            <span className="wk-timeline__when">{(a.Created_On || "").replace("T", " ").slice(0, 16)}</span>
          </li>
        ))}
      </ul>

      {packOpen ? <ReadyPack api={api} itemId={itemId} onClose={() => setPackOpen(false)} onPosted={changed} /> : null}
    </Drawer>
  );
}

export default ItemDrawer;
